import { useEffect, useRef, useState } from "react";
import { Mic, Square, Loader2 } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";

/**
 * Voice dictation button.
 * Records via MediaRecorder (webm), uploads to /api/ai/transcribe,
 * calls onTranscribed(text) with the appended transcript.
 */
export default function MicButton({ onTranscribed, disabled }) {
  const [state, setState] = useState("idle"); // idle | recording | processing
  const mediaRef = useRef(null);
  const chunksRef = useRef([]);
  const streamRef = useRef(null);

  useEffect(() => {
    return () => {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((t) => t.stop());
      }
    };
  }, []);

  const start = async () => {
    if (disabled) return;
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const mime = MediaRecorder.isTypeSupported("audio/webm")
        ? "audio/webm"
        : "";
      const mr = new MediaRecorder(stream, mime ? { mimeType: mime } : undefined);
      mediaRef.current = mr;
      chunksRef.current = [];
      mr.ondataavailable = (e) => {
        if (e.data && e.data.size > 0) chunksRef.current.push(e.data);
      };
      mr.onstop = async () => {
        try {
          const blob = new Blob(chunksRef.current, { type: mime || "audio/webm" });
          if (blob.size === 0) {
            setState("idle");
            return;
          }
          setState("processing");
          const fd = new FormData();
          fd.append("audio", blob, "recording.webm");
          const { data } = await api.post("/ai/transcribe", fd, {
            headers: { "Content-Type": "multipart/form-data" },
          });
          if (data?.text) {
            onTranscribed(data.text);
            toast.success("Transcribed");
          } else {
            toast.error("No speech detected");
          }
        } catch (err) {
          const msg = err?.response?.data?.detail || "Transcription failed";
          toast.error(msg);
        } finally {
          setState("idle");
          streamRef.current?.getTracks().forEach((t) => t.stop());
          streamRef.current = null;
        }
      };
      mr.start();
      setState("recording");
    } catch (err) {
      toast.error("Microphone permission denied");
      setState("idle");
    }
  };

  const stop = () => {
    if (mediaRef.current && state === "recording") {
      mediaRef.current.stop();
    }
  };

  const onClick = () => {
    if (state === "idle") start();
    else if (state === "recording") stop();
  };

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled || state === "processing"}
      data-testid="mic-button"
      className={`relative inline-flex items-center justify-center gap-2 rounded-xl px-4 py-2.5 text-sm font-medium transition-all
        ${state === "recording"
          ? "bg-clinical-accent text-white animate-pulse-mic"
          : state === "processing"
          ? "bg-clinical-accent/15 text-clinical-accent"
          : "bg-white border border-clinical-primary/20 text-clinical-primary hover:border-clinical-primary/50"}`}
    >
      {state === "idle" && (
        <>
          <Mic className="h-4 w-4" />
          Dictate
        </>
      )}
      {state === "recording" && (
        <>
          <Square className="h-4 w-4 fill-current" />
          Stop
        </>
      )}
      {state === "processing" && (
        <>
          <Loader2 className="h-4 w-4 animate-spin" />
          Transcribing…
        </>
      )}
    </button>
  );
}
